'use strict';

var assign = require('lodash/assign');

var nextId = require('bpmn-js-properties-panel/lib/Utils').nextId;

/**
 * Create an input parameter representing the given
 * binding and value.
 *
 * @param {PropertyBinding} binding
 * @param {String} value
 * @param {BpmnFactory} bpmnFactory
 *
 * @return {ModdleElement}
 */
function createInputParameter(binding, value, bpmnFactory) {
  var scriptFormat = binding.scriptFormat,
      parameterValue,
      parameterDefinition;

  if (scriptFormat) {
    parameterDefinition = bpmnFactory.create('flowable:Script', {
      scriptFormat: scriptFormat,
      value: value
    });
  } else {
    parameterValue = value;
  }

  return bpmnFactory.create('flowable:InputParameter', {
    name: binding.name,
    value: parameterValue,
    definition: parameterDefinition
  });
}

module.exports.createInputParameter = createInputParameter;


/**
 * Create an output parameter representing the given
 * binding and value.
 *
 * @param {PropertyBinding} binding
 * @param {String} value
 * @param {BpmnFactory} bpmnFactory
 *
 * @return {ModdleElement}
 */
function createOutputParameter(binding, value, bpmnFactory) {
  var scriptFormat = binding.scriptFormat,
      parameterValue,
      parameterDefinition;

  if (scriptFormat) {
    parameterDefinition = bpmnFactory.create('flowable:Script', {
      scriptFormat: scriptFormat,
      value: binding.source
    });
  } else {
    parameterValue = binding.source;
  }

  return bpmnFactory.create('flowable:OutputParameter', {
    name: value,
    value: parameterValue,
    definition: parameterDefinition
  });
}

module.exports.createOutputParameter = createOutputParameter;


/**
 * Create flowable property from the given binding.
 *
 * @param {PropertyBinding} binding
 * @param {String} value
 * @param {BpmnFactory} bpmnFactory
 *
 * @return {ModdleElement}
 */
function createFlowableProperty(binding, value, bpmnFactory) {
  return bpmnFactory.create('flowable:Property', {
    name: binding.name,
    value: value || ''
  });
}

module.exports.createFlowableProperty = createFlowableProperty;


/**
 * Create flowable:in element from given binding.
 *
 * @param {PropertyBinding} binding
 * @param {String} value
 * @param {BpmnFactory} bpmnFactory
 *
 * @return {ModdleElement}
 */
function createFlowableIn(binding, value, bpmnFactory) {

  var properties = createFlowableInOutAttrs(binding, value);

  return bpmnFactory.create('flowable:In', properties);
}

module.exports.createFlowableIn = createFlowableIn;


/**
 * Create flowable:in with businessKey element from given binding.
 *
 * @param {PropertyBinding} binding
 * @param {String} value
 * @param {BpmnFactory} bpmnFactory
 *
 * @return {ModdleElement}
 */
function createFlowableInWithBusinessKey(binding, value, bpmnFactory) {
  return bpmnFactory.create('flowable:In', {
    businessKey: value
  });
}

module.exports.createFlowableInWithBusinessKey = createFlowableInWithBusinessKey;


/**
 * Create flowable:out element from given binding.
 *
 * @param {PropertyBinding} binding
 * @param {String} value
 * @param {BpmnFactory} bpmnFactory
 *
 * @return {ModdleElement}
 */
function createFlowableOut(binding, value, bpmnFactory) {
  var properties = createFlowableInOutAttrs(binding, value);

  return bpmnFactory.create('flowable:Out', properties);
}

module.exports.createFlowableOut = createFlowableOut;


/**
 * Create flowable:executionListener element containing an inline script from given binding.
 *
 * @param {PropertyBinding} binding
 * @param {String} value
 * @param {BpmnFactory} bpmnFactory
 *
 * @return {ModdleElement}
 */
function createFlowableExecutionListenerScript(binding, value, bpmnFactory) {
  var scriptFormat = binding.scriptFormat,
      parameterValue,
      parameterDefinition;

  if (scriptFormat) {
    parameterDefinition = bpmnFactory.create('flowable:Script', {
      scriptFormat: scriptFormat,
      value: value
    });
  } else {
    parameterValue = value;
  }

  return bpmnFactory.create('flowable:ExecutionListener', {
    event: binding.event,
    value: parameterValue,
    script: parameterDefinition
  });
}

module.exports.createFlowableExecutionListenerScript = createFlowableExecutionListenerScript;

/**
 * Create flowable:field element containing string or expression from given binding.
 *
 * @param {PropertyBinding} binding
 * @param {String} value
 * @param {BpmnFactory} bpmnFactory
 *
 * @return {ModdleElement}
 */
function createFlowableFieldInjection(binding, value, bpmnFactory) {
  var DEFAULT_PROPS = {
    'string': undefined,
    'expression': undefined,
    'name': undefined
  };

  var props = assign({}, DEFAULT_PROPS);

  if (!binding.expression) {
    props.string = value;
  } else {
    props.expression = value;
  }
  props.name = binding.name;

  return bpmnFactory.create('flowable:Field', props);
}

module.exports.createFlowableFieldInjection = createFlowableFieldInjection;

/**
 * Create flowable:errorEventDefinition element containing expression and errorRef
 * from given binding.
 *
 * @param {PropertyBinding} binding
 * @param {String} value
 * @param {ModdleElement} error
 * @param {ModdleElement} parent
 * @param {BpmnFactory} bpmnFactory
 *
 * @return {ModdleElement}
 */
function createFlowableErrorEventDefinition(binding, value, error, parent, bpmnFactory) {
  var errorRef = error,
      expression = value;

  var newErrorEventDefinition = bpmnFactory.create('flowable:ErrorEventDefinition', {
    expression: expression,
    errorRef: errorRef
  });

  newErrorEventDefinition.$parent = parent;

  return newErrorEventDefinition;
}

module.exports.createFlowableErrorEventDefinition = createFlowableErrorEventDefinition;

/**
 * Create bpmn:error element containing a specific error id given by a binding.
 *
 * @param {String} bindingErrorRef
 * @param {ModdleElement} parent
 * @param {BpmnFactory} bpmnFactory
 *
 * @return { ModdleElement }
 */
function createError(bindingErrorRef, parent, bpmnFactory) {
  var error = bpmnFactory.create('bpmn:Error', {

    // we need to later retrieve the error from a binding
    id: nextId('Error_' + bindingErrorRef + '_')
  });

  error.$parent = parent;

  return error;
}

module.exports.createError = createError;

// helpers ////////////////////////////

/**
 * Create properties for flowable:in and flowable:out types.
 */
function createFlowableInOutAttrs(binding, value) {

  var properties = {};

  // Explicitly cover all conditions as specified here:
  // https://github.com/flowable/flowable-modeler/blob/develop/docs/element-templates/README.md#flowablein
  if (binding.type === 'flowable:in') {
    if (binding.target && !binding.expression && !binding.variables) {
      properties.target = binding.target;
      properties.source = value;

    } else if (binding.target && binding.expression === true && !binding.variables) {
      properties.target = binding.target;
      properties.sourceExpression = value;

    } else if (!binding.target && !binding.expression && binding.variables === 'local') {
      properties.local = true;
      properties.variables = 'all';

    } else if (binding.target && !binding.expression && binding.variables === 'local') {
      properties.local = true;
      properties.source = value;
      properties.target = binding.target;

    } else if (binding.target && binding.expression && binding.variables === 'local') {
      properties.local = true;
      properties.sourceExpression = value;
      properties.target = binding.target;

    } else if (!binding.target && !binding.expression && binding.variables === 'all') {
      properties.variables = 'all';
    } else {
      throw new Error('invalid configuration for flowable:in element template binding');
    }
  }

  // Explicitly cover all conditions as specified here:
  // https://github.com/flowable/flowable-modeler/blob/develop/docs/element-templates/README.md#flowableout
  if (binding.type === 'flowable:out') {
    if (binding.source && !binding.sourceExpression && !binding.variables) {
      properties.target = value;
      properties.source = binding.source;

    } else if (!binding.source && binding.sourceExpression && !binding.variables) {
      properties.target = value;
      properties.sourceExpression = binding.sourceExpression;

    } else if (!binding.source && !binding.sourceExpression && binding.variables === 'all') {
      properties.variables = 'all';

    } else if (binding.source && !binding.sourceExpression && binding.variables === 'local') {
      properties.local = true;
      properties.source = binding.source;
      properties.target = value;

    } else if (!binding.source && binding.sourceExpression && binding.variables === 'local') {
      properties.local = true;
      properties.sourceExpression = binding.sourceExpression;
      properties.target = value;

    } else if (!binding.source && !binding.sourceExpression && binding.variables === 'local') {
      properties.local = true;
      properties.variables = 'all';
    } else {
      throw new Error('invalid configuration for flowable:out element template binding');
    }
  }

  return properties;
}
